import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import DOMPurify from "dompurify";

function UserProfile() {
  const [groupedByUser, setGroupedByUser] = useState({});
  const [userAnsweredQuestions, setUserAnsweredQuestions] = useState([]);
  const user = JSON.parse(localStorage.getItem("UserDetails"));
  const navigate = useNavigate();

  useEffect(() => {
    async function getQuestions() {
      try {
        const response = await axios.get("/api/question", {
          baseURL: process.env.REACT_APP_FORUM_API,
        });
        const allQuestions = response.data;

        // Filter questions asked by the logged-in user
        const filteredQuestions = allQuestions.filter((question) => {
          return question.user?.userName === user.userName;
        });

        // Group filtered questions by username
        const groupedQuestions = filteredQuestions.reduce((acc, question) => {
          const userName = question.user?.userName || "Unknown User";
          if (!acc[userName]) acc[userName] = [];
          acc[userName].push(question);
          return acc;
        }, {});

        setGroupedByUser(groupedQuestions);
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    }
    getQuestions();
  }, [user.userName]);

  useEffect(() => {
    async function getAnswers() {
      try {
        const response = await axios.get("/api/answer", {
          baseURL: process.env.REACT_APP_FORUM_API,
        });

        const allAnswers = response.data;
        const filteredAnswers = allAnswers.filter((answer) => {
          return answer.user?.userName === user?.userName;
        });

        setUserAnsweredQuestions(filteredAnswers);
      } catch (error) {
        console.error("Error fetching answers:", error);
      }
    }
    getAnswers();
  }, [user?.userName]);

  const getFirst20Words = (text) => {
    const words = text.split(/\s+/);
    return words.slice(0, 15).join(" ") + (words.length > 15 ? "..." : "");
  };

  return (
    <Box sx={{ overflow: "auto" }}>
      {Object.keys(groupedByUser).length > 0 ? (
        Object.entries(groupedByUser).map(([userName, questions], index) => (
          <Grid container spacing={3} key={index}>
            {/* Left Column - Stats and Communities */}
            <Grid item xs={12} md={3}>
              <Typography variant="h6" gutterBottom>
                Stats
              </Typography>
              <Card sx={{ padding: 2, marginBottom: 3 }}>
                <CardContent
                  sx={{ display: "", justifyContent: "space-between" }}
                >
                  <Typography variant="body1">
                    <strong>Questions:</strong> {questions.length || 0}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Answers:</strong>{" "}
                    {userAnsweredQuestions.length || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column - Posted Questions and Answers */}
            <Grid item xs={12} md={9} sx={{ overflow: "auto" }}>
              <Typography variant="p1" gutterBottom sx={{ fontWeight: "bold" }}>
                Posted Questions
              </Typography>

              {questions.map((question) => {
                const formattedDate = new Date(
                  question.created_at
                ).toLocaleString();

                return (
                  <Card
                    sx={{ padding: 0.5, marginBottom: 1, cursor: "pointer" }}
                    key={question._id}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        "&:last-child": { paddingBottom: "8px" },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: "16px",
                          lineHeight: 1.2,
                          fontWeight: "bold",
                        }}
                      >
                        <Link
                          to={`/question?q=${question?._id}`}
                          style={{ color: "#1976d2" }} // Set color here
                        >
                          {question.title}
                        </Link>
                      </Typography>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "14px", lineHeight: 1.2 }}
                      >
                        {formattedDate}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}

              <Typography variant="p1" gutterBottom sx={{ fontWeight: "bold" }}>
                Answered Questions
              </Typography>

              {userAnsweredQuestions.map((question) => {
                const formattedDate = new Date(
                  question.created_at
                ).toLocaleString();
                return (
                  <Card
                    sx={{ padding: 0.5, marginBottom: 1 }}
                    key={question._id}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        "&:last-child": { paddingBottom: "8px" },
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{
                          flex: 1,
                          fontSize: "16px",
                          lineHeight: 1.2,
                        }}
                      >
                        <Link
                          to={`/question?q=${question?._id}`} // Assuming you have a separate route for answers
                          style={{ color: "#1976d2", textDecoration: "none" }}
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(
                                getFirst20Words(question.answer)
                              ),
                            }}
                          />
                        </Link>
                      </Typography>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ fontSize: "14px", lineHeight: 1.2 }}
                      >
                        {formattedDate}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Grid>
          </Grid>
        ))
      ) : (
        <Typography variant="body1">No questions found</Typography>
      )}
    </Box>
  );
}

export default UserProfile;
